import { isProblem } from '@/lib/api/problem';

/**
 * The BP-05 problem URNs a screen branches on, copied verbatim from
 * `api/app/modules/sessions/problems.py` and `commission/invoice_source.py`.
 *
 * `lib/api/problem.ts`'s `PROBLEM` map is BP-01's and this build plan does not edit it, so
 * the session URNs live with the feature that reacts to them — the same arrangement
 * `features/scheduling/problems.ts` made for BP-04.
 */
export const SESSION_PROBLEM = {
  illegalTransition: 'urn:26ad:session-illegal-transition',
  versionConflict: 'urn:26ad:session-version-conflict',
  materialsEdited: 'urn:26ad:session-materials-edited',
  inventoryUnavailable: 'urn:26ad:session-inventory-unavailable',
  notFound: 'urn:26ad:session-not-found',
  noActiveStep: 'urn:26ad:session-no-active-step',
  skuNotConsumable: 'urn:26ad:session-sku-not-consumable',
  materialNotFound: 'urn:26ad:session-material-not-found',
  confirmRequired: 'urn:26ad:session-confirm-required',
  materialsUnconfirmed: 'urn:26ad:session-materials-unconfirmed',
  nothingToRepeat: 'urn:26ad:session-nothing-to-repeat',
  ratingTokenInvalid: 'urn:26ad:session-rating-token-invalid',
  ruleInvalid: 'urn:26ad:commission-rule-invalid',
  ruleNotFound: 'urn:26ad:commission-rule-not-found',
  statementForbidden: 'urn:26ad:commission-statement-forbidden',
  noInvoiceSource: 'urn:26ad:commission-invoice-source-missing',
  invoiceNotFound: 'urn:26ad:commission-invoice-not-found',
} as const;

/** True when another tablet wrote first and this one is holding a stale `version` (§9). */
export function isVersionConflict(error: unknown): boolean {
  return isProblem(error) && error.type === SESSION_PROBLEM.versionConflict;
}

/**
 * True when completion failed because BP-02 could not take the stock (§7).
 *
 * The session stays `in_progress` and the dialog offers a retry: the transaction rolled
 * back, so nothing was half-consumed and pressing again is safe.
 */
export function isInventoryUnavailable(error: unknown): boolean {
  return isProblem(error) && error.type === SESSION_PROBLEM.inventoryUnavailable;
}

/** True when "Repeat last set" found the current materials already edited (R11). */
export function isMaterialsEdited(error: unknown): boolean {
  return isProblem(error) && error.type === SESSION_PROBLEM.materialsEdited;
}

/** True when this client has no previous set to repeat (R11). */
export function isNothingToRepeat(error: unknown): boolean {
  return isProblem(error) && error.type === SESSION_PROBLEM.nothingToRepeat;
}

/** True when BP-06 has not registered an invoice source yet — a 501, not an empty answer. */
export function isNoInvoiceSource(error: unknown): boolean {
  return isProblem(error) && error.type === SESSION_PROBLEM.noInvoiceSource;
}

/** True when the rating link has expired or was never minted (R13). */
export function isRatingTokenInvalid(error: unknown): boolean {
  return isProblem(error) && error.type === SESSION_PROBLEM.ratingTokenInvalid;
}

/** The api's own sentence for a problem, or `fallback` for anything else. */
export function problemMessage(error: unknown, fallback: string): string {
  return isProblem(error) ? (error.detail ?? error.title) : fallback;
}

/**
 * What to draw for a failed call, or `null` when nothing failed.
 *
 * TanStack Query reports "no error" as `null` and a plain `useState` as `undefined`; every
 * screen here would otherwise spell that pair out again, and the second half of it would
 * never run in a test.
 */
export function failureMessage(error: unknown, fallback: string): string | null {
  if (error === null || error === undefined) {
    return null;
  }
  return problemMessage(error, fallback);
}
